import { z } from 'zod';
import { googleKeywordTextSchema } from './google-keyword.js';
// Ad-platform schemas — Phase A of the takeover flow. The shapes here are
// platform-neutral: a campaign snapshot fits Google Ads today, and we
// want it to fit Meta/TikTok later without a refactor. Anything provider-
// specific lives inside the `providerRaw` jsonb escape hatch.
export const adPlatformSchema = z.enum(['google', 'meta', 'tiktok']);
/** Aggregated 30-day performance metrics. All fields are required so we
 *  don't fall back to "—" in the UI; the client emits zeros when nothing
 *  has happened in the window. */
export const campaignMetricsSchema = z.object({
    windowDays: z.number().int().min(1).max(365),
    impressions: z.number().int().min(0),
    clicks: z.number().int().min(0),
    ctr: z.number().min(0).max(1),
    conversions: z.number().min(0),
    conversionRate: z.number().min(0).max(1),
    costUsd: z.number().min(0),
    costPerConversionUsd: z.number().min(0),
    /** Days within the window the campaign hit its daily budget cap. Useful
     *  signal for "scale up" vs "audience is tapped" recommendations. */
    daysBudgetCapped: z.number().int().min(0),
});
/** Ceiling on the per-ad-group keyword array. A snapshot is persisted
 *  to campaigns.snapshot as jsonb, and an imported account can carry
 *  thousands of keywords in one ad group; keywordCount stays
 *  authoritative for totals so truncation costs detail, not truth. */
export const KEYWORD_SNAPSHOT_CAP = 200;
/** Spec 3c98725c — one keyword and what the platform says about its
 *  ability to serve. This is the evidence behind "can this campaign
 *  actually work", as opposed to what it spent.
 *
 *  Every field here was verified live against Google Ads API v25.
 *  Two that look useful and are NOT here, recorded so nobody re-tries:
 *  `quality_info.*` comes back EMPTY until the ad has impressions (so
 *  it is absent on exactly the campaigns most in need of a health
 *  read), and `ad_group_criterion.policy_summary` is not a field on
 *  v25 at all — the query 400s, and `approvalStatus` covers it. */
export const keywordSnapshotSchema = z.object({
    /** Numeric ad_group_criterion id. Required to ACT on the keyword —
     *  google addresses ad-group criteria by a compound
     *  {ad_group_id}~{criterion_id} resource name, so a surface that
     *  offers "pause this keyword" cannot do it from the text alone. */
    criterionId: z.string().min(1).max(120),
    /** A BARE CHARACTER BOUND, DELIBERATELY — not googleKeywordTextSchema.
     *
     *  This is a READ shape. The write-bound keyword fields enforce Google's
     *  10-word limit as well, because a proposal that breaks it takes a whole
     *  atomic mutate down; here the keyword already exists in somebody's
     *  account. An imported or legacy account can hold a keyword we would never
     *  write, and a read that refuses to parse it blinds every health surface on
     *  that campaign — the campaign disappears rather than the keyword.
     *
     *  Strict about what we send, permissive about what we are told. */
    text: z.string().min(1).max(80),
    matchType: z.enum(['EXACT', 'PHRASE', 'BROAD', 'UNKNOWN']),
    status: z.enum(['enabled', 'paused', 'removed']),
    /** Google's `system_serving_status`. 'rarely_served' is its word for
     *  "essentially nobody searches this" — the signal that catches a
     *  keyword an agent invented off a brief rather than researched. */
    systemServingStatus: z.enum(['eligible', 'rarely_served', 'unknown']),
    approvalStatus: z
        .enum(['approved', 'approved_limited', 'disapproved', 'area_of_interest_only', 'unknown'])
        .optional(),
    /** The bid actually in force, and where it comes from. Today this
     *  reads AD_GROUP for every Mag-built campaign, which is why the
     *  only bid lever in v1 is ad-group level. */
    effectiveCpcBidUsd: z.number().min(0).optional(),
    effectiveCpcBidSource: z.string().min(1).max(40).optional(),
    /** What Google estimates a click must cost to reach the first page /
     *  top of page.
     *
     *  ABSENT is meaningful and must never be coerced to 0. Google omits
     *  these entirely for a keyword it will not serve, so a missing
     *  estimate means "no auction to be under-priced for", NOT "your bid
     *  is fine" — reading it as zero would make every dead keyword look
     *  correctly bid. */
    firstPageCpcUsd: z.number().min(0).optional(),
    topOfPageCpcUsd: z.number().min(0).optional(),
});
export const adGroupSnapshotSchema = z.object({
    providerAdGroupId: z.string().min(1).max(120),
    name: z.string().min(1).max(160),
    status: z.enum(['enabled', 'paused', 'removed']),
    /** Bidding strategy / max CPC summary, free-form so each provider can
     *  drop its own wording. */
    biddingSummary: z.string().min(1).max(200),
    /** Keyword count (Google) or audience count (Meta).
     *
     *  AUTHORITATIVE for totals, independently of `keywords` below. That
     *  array is capped, so a large ad group lists fewer than it has —
     *  this number keeps meaning "how many EXIST", never "how many we
     *  listed". */
    keywordCount: z.number().int().min(0),
    /** Per-keyword serving evidence, capped. Optional so snapshots
     *  written before this landed parse cleanly; absent means "we didn't
     *  look", never "no keywords". */
    keywords: z.array(keywordSnapshotSchema).max(KEYWORD_SNAPSHOT_CAP).optional(),
    metrics: campaignMetricsSchema,
});
/** [12.2L-112] Per-ad policy status from the ad-platform API. Lifted
 *  from Google's PolicyTopicEntry shape; meta has its own equivalent
 *  when imports land there. Surfaced on the takeover view + drives
 *  the policy_issue feed cards + notifications.
 *
 *  approvalStatus: APPROVED, APPROVED_LIMITED, DISAPPROVED,
 *    AREA_OF_INTEREST_ONLY, or UNKNOWN. Lowercase + snake_case so
 *    the dashboard renders without translation.
 *  reviewStatus: UNDER_REVIEW or REVIEWED.
 *  topics: One entry per policy violation. Empty array when approved.
 *    Topic + type strings are stored verbatim from Google so new
 *    enum values don't require a schema migration. */
export const adPolicySummarySchema = z.object({
    approvalStatus: z.enum([
        'approved',
        'approved_limited',
        'disapproved',
        'area_of_interest_only',
        'unknown',
    ]),
    /** Google's PolicyReviewStatus, lowercased. The member list mirrors
     *  the API enum exactly — REVIEW_IN_PROGRESS / REVIEWED /
     *  UNDER_APPEAL / ELIGIBLE_MAY_SERVE, plus 'unknown' for
     *  UNSPECIFIED/UNKNOWN and anything new Google adds.
     *
     *  This previously read `'under_review'`, which is not a value the
     *  Google Ads API ever sends — it was guessed. Google sends
     *  REVIEW_IN_PROGRESS, so every ad awaiting review mapped to
     *  'unknown' and the reviewing state was invisible to us. Verified
     *  against a live account (campaign 24254112417 returned
     *  reviewStatus REVIEW_IN_PROGRESS while the Ads UI showed the ad
     *  in review). No stored row can carry the old member, since nothing
     *  ever produced it. */
    reviewStatus: z.enum([
        'review_in_progress',
        'reviewed',
        'under_appeal',
        'eligible_may_serve',
        'unknown',
    ]),
    topics: z
        .array(z.object({
        /** PolicyTopicEntry.topic — google enum string like
         *  "DESTINATION_NOT_WORKING". */
        topic: z.string().min(1).max(120),
        /** PolicyTopicEntry.type — PROHIBITED / LIMITED /
         *  FULLY_LIMITED / DESCRIPTIVE / BROADENING. */
        type: z.string().min(1).max(32),
        /** Per-evidence detail strings — URLs, text snippets, country
         *  constraints. Free-form because the shape varies per topic. */
        evidences: z.array(z.string().max(500)).max(20),
    }))
        .max(10),
});
export const adCreativeSnapshotSchema = z.object({
    providerAdId: z.string().min(1).max(120),
    /** Type label — "responsive search ad", "image ad", "video ad". */
    type: z.string().min(1).max(60),
    /** Mostly-cosmetic name field shown next to each ad in the takeover view. */
    name: z.string().min(1).max(160),
    status: z.enum(['enabled', 'paused', 'removed']),
    /** Up to ~15 headlines (Google RSAs) or one body line (image/video). */
    headlines: z.array(z.string().min(1).max(120)).max(20),
    descriptions: z.array(z.string().min(1).max(240)).max(10),
    /** Google's Ad Strength rating, when available — used as a quality
     *  proxy in the takeover read. Null for platforms that don't expose it. */
    adStrength: z.enum(['poor', 'average', 'good', 'excellent']).nullable(),
    metrics: campaignMetricsSchema,
    /** [12.2L-112] Per-ad approval + policy violations from the platform.
     *  Optional — legacy snapshots written before the field landed
     *  parse cleanly without it; the policy detector treats absence as
     *  "no data yet" rather than "ad is approved." */
    policySummary: adPolicySummarySchema.optional(),
    /** [12.2L-112] Final URL on the ad. Surfaced so mag can mirror
     *  it on her drafts (create_rsa, swap_rsa) instead of defaulting
     *  to the brand marketing URL root. Optional for legacy snapshots. */
    finalUrl: z.string().max(2000).optional(),
    /** [12.2L-116] Parent ad group's providerAdGroupId. Surfaced so
     *  remediation synthesis (and the dashboard's manual swap modal)
     *  can target the correct ad group on multi-ad-group campaigns.
     *  Without this, both surfaces fall back to "use the campaign's
     *  first ad group" which is wrong on any campaign with more than
     *  one. Optional for legacy snapshots — both fallbacks still work
     *  on the single-ad-group case where they happen to be correct. */
    providerAdGroupId: z.string().min(1).max(120).optional(),
});
/** Campaign-level KPI band for the takeover view's top strip. Carries
 *  last-7-day + prior-7-day aggregates so the UI can render
 *  "$X spent · -23% vs prior 7d" without re-deriving from the 30d
 *  metric. Optional on the snapshot so legacy imports parse cleanly;
 *  the band collapses to "not enough history yet" when absent or
 *  zeroed. */
export const campaignKpiBandSchema = z.object({
    last7d: campaignMetricsSchema,
    prior7d: campaignMetricsSchema,
});
/** Day-of-week label, lowercase. Mirrors Google Ads' DayOfWeek enum
 *  (MONDAY..SUNDAY) lowercased so the takeover-read agent reads
 *  natural English without translation. */
export const dayOfWeekSchema = z.enum([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
]);
/** One row per day of week with metrics aggregated over the campaign
 *  snapshot's 30-day window. The takeover-read agent uses these to
 *  spot scheduling patterns ("weekends do nothing", "tuesdays carry
 *  the campaign") that the flat 30d total would obscure. */
export const campaignDayOfWeekRowSchema = z.object({
    day: dayOfWeekSchema,
    impressions: z.number().int().min(0),
    clicks: z.number().int().min(0),
    conversions: z.number().min(0),
    costUsd: z.number().min(0),
});
/** Day-of-week breakdown for a campaign — exactly 7 rows in
 *  monday → sunday order so consumers can index by day name or
 *  position. windowDays is the same window the per-row metrics
 *  cover (typically 30, matching the rest of the snapshot). */
export const campaignDayOfWeekBreakdownSchema = z.object({
    windowDays: z.number().int().min(1).max(365),
    rows: z.array(campaignDayOfWeekRowSchema).length(7),
});
/** One row per hour-of-day (0-23) with metrics aggregated over the
 *  30d window. Mag uses these to spot peak-hour patterns inside the
 *  scheduled window ("conversions cluster 6-9pm — push more spend
 *  there"). Hour values follow the customer's timezone. */
export const campaignHourOfDayRowSchema = z.object({
    /** 0-23. */
    hour: z.number().int().min(0).max(23),
    impressions: z.number().int().min(0),
    clicks: z.number().int().min(0),
    conversions: z.number().min(0),
    costUsd: z.number().min(0),
});
/** Hour-of-day breakdown — exactly 24 rows in 0..23 order. Hours
 *  with zero activity in the window get zeroed rows so consumers
 *  can always index by position. */
export const campaignHourOfDayBreakdownSchema = z.object({
    windowDays: z.number().int().min(1).max(365),
    rows: z.array(campaignHourOfDayRowSchema).length(24),
});
/** One ad-schedule entry from Google Ads' `campaign_criterion` of
 *  type AD_SCHEDULE — a single (day, start-time, end-time) window
 *  during which the campaign is allowed to serve. A campaign with
 *  no entries (the field is absent from the snapshot or `entries`
 *  is empty) runs 24/7. Multiple entries can overlap or chain
 *  (e.g. Mon-Fri 09:00-12:00 + Mon-Fri 13:00-18:00). Minutes are
 *  constrained to Google's quarter-hour grid: 0 / 15 / 30 / 45. */
export const campaignAdScheduleEntrySchema = z.object({
    dayOfWeek: dayOfWeekSchema,
    /** 0-23. */
    startHour: z.number().int().min(0).max(23),
    /** Google's minute enum: 0 / 15 / 30 / 45. */
    startMinute: z.union([z.literal(0), z.literal(15), z.literal(30), z.literal(45)]),
    /** 0-24 — Google allows 24 as the "end of day" sentinel. */
    endHour: z.number().int().min(0).max(24),
    endMinute: z.union([z.literal(0), z.literal(15), z.literal(30), z.literal(45)]),
    /** Bid modifier (e.g. 1.2 = +20% bid during this window, 0.5 = -50%).
     *  Null when the founder didn't set one — google treats null as 1.0. */
    bidModifier: z.number().min(0).max(10).nullable(),
});
/** Campaign-level ad-schedule — the full list of windows the founder
 *  has configured on Google. Empty `entries` = "no schedule, runs
 *  24/7". The takeover-read agent must combine this with the
 *  dayOfWeekBreakdown to correctly read zero-performance days
 *  (scheduled-off vs actually-performing-badly). */
export const campaignAdScheduleSchema = z.object({
    entries: z.array(campaignAdScheduleEntrySchema).max(168),
});
/** Google's CampaignPrimaryStatus — what the Ads UI prints as the
 *  campaign's "Estado". Distinct from `status` (ENABLED/PAUSED/
 *  REMOVED, the setting) — this is whether the campaign is actually
 *  SERVING, which is the question a founder is really asking.
 *
 *  `reasons` carries CampaignPrimaryStatusReason verbatim from Google
 *  (e.g. MOST_ADS_UNDER_REVIEW, CAMPAIGN_PENDING, BIDDING_STRATEGY_
 *  MISCONFIGURED). Left as free-form strings rather than an enum: the
 *  list is long, Google extends it, and an unknown reason must render
 *  as "not serving, reason we don't recognize" rather than fail a
 *  snapshot parse. */
export const campaignServingStatusSchema = z.object({
    primaryStatus: z.enum([
        'eligible',
        'paused',
        'removed',
        'ended',
        'pending',
        'misconfigured',
        'limited',
        'learning',
        'unknown',
    ]),
    reasons: z.array(z.string().min(1).max(80)).max(20),
});
/** [task dd14991] AD ASSETS — sitelinks, structured snippets, callouts and
 *  images attached to a campaign.
 *
 *  THE CONCEPT WAS ABSENT, not merely unwritten. Before this, an ad snapshot
 *  modelled headlines, descriptions and a finalUrl and nothing else, so no
 *  surface could report that a campaign had no sitelinks and no agent could
 *  reason about it — google's own Recommendations page was the only place the
 *  gap was visible, and nothing read that either (task 8f6a2fb).
 *
 *  Assets hang off the CAMPAIGN (or ad group), not off an ad. That is why they
 *  are modelled here rather than on adCreativeSnapshotSchema, and it has a
 *  useful consequence: `swapResponsiveSearchAd`'s remove+create destroys an
 *  ad's learning history but does NOT destroy its campaign's assets.
 *
 *  `fieldType` is verbatim from google (SITELINK, STRUCTURED_SNIPPET, CALLOUT,
 *  MARKETING_IMAGE, …) and deliberately not an enum, for the same reason
 *  biddingStrategyType is not: google extends the list, and an unrecognised
 *  type must read as "something we don't model" rather than fail the whole
 *  snapshot parse. */
export const campaignAssetSnapshotSchema = z.object({
    /** Google's own field_type, verbatim. */
    fieldType: z.string().min(1).max(60),
    /** ENABLED / PAUSED / REMOVED, verbatim. */
    status: z.string().min(1).max(40).optional(),
    /** The human-readable text, where the asset has one: a sitelink's link
     *  text, a structured snippet's header, a callout's text. Absent for assets
     *  whose content is not text (an image), which is a fact about the asset
     *  rather than a failure to read it. */
    text: z.string().min(1).max(200).optional(),
});
/** Google's own caps on the two asset kinds Mag authors, both CONFIRMED BY
 *  DRY RUN against a live account rather than read off a doc: a 25-character
 *  callout is accepted and 26 is refused with TOO_LONG (theory 0bdb82d).
 *
 *  Here rather than beside either consumer because BOTH need them and they
 *  sit on opposite sides of a package boundary — the agent that writes the
 *  copy (packages/agents) and the client that sends it (apps/api) must agree,
 *  and a second copy in either is a number that can drift from google's. */
export const MAX_CALLOUT_CHARS = 25;
export const MAX_SNIPPET_VALUE_CHARS = 25;
export const campaignSnapshotSchema = z.object({
    providerCampaignId: z.string().min(1).max(120),
    /** The campaign name as it appears in Google Ads / Meta Ads Manager. */
    name: z.string().min(1).max(200),
    platform: adPlatformSchema,
    /** Search / Display / Performance Max / etc. — free-form provider string. */
    type: z.string().min(1).max(60),
    status: z.enum(['enabled', 'paused', 'removed']),
    /** Whether the campaign is actually serving, per the platform.
     *  Optional — snapshots written before this landed parse cleanly,
     *  and absent must read as "we didn't look", never as "serving".
     *  Meta has no equivalent field today and leaves it unset. */
    servingStatus: campaignServingStatusSchema.optional(),
    /** How the platform says the campaign bids right now — e.g.
     *  MANUAL_CPC, MAXIMIZE_CONVERSIONS, TARGET_CPA. Verbatim from the
     *  provider and deliberately not an enum: Google extends the list,
     *  and an unrecognised strategy must read as "something we don't
     *  model" rather than fail the whole snapshot parse — the same
     *  reasoning as servingStatus.reasons above.
     *
     *  Optional, and absent means "we did not look", never "none". Older
     *  snapshots and Meta both leave it unset. */
    biddingStrategyType: z.string().min(1).max(60).optional(),
    /** Daily budget in USD. */
    dailyBudgetUsd: z.number().min(0).max(100_000),
    /** ISO date of the most recent change Mag would care about (for the
     *  "last touched" line in the takeover view). */
    lastModifiedAt: z.string(),
    metrics: campaignMetricsSchema,
    /** Campaign-level KPI band for the takeover view's WoW strip.
     *  Optional — legacy snapshots written before the field landed
     *  parse cleanly; the UI hides the WoW row and shows the 30d
     *  fallback instead. */
    kpiBand: campaignKpiBandSchema.optional(),
    /** Day-of-week metrics breakdown for the takeover-read agent's
     *  scheduling-pattern read. Optional — legacy snapshots parse
     *  cleanly without it; the agent prompt explicitly handles the
     *  absent case. */
    dayOfWeekBreakdown: campaignDayOfWeekBreakdownSchema.optional(),
    /** Hour-of-day metrics breakdown — 24 rows for the customer-tz
     *  hour buckets. Mag uses this with adSchedule to read peak hours
     *  ("conversions cluster 6-9pm"). Optional + same legacy handling
     *  as the day-of-week field. */
    hourOfDayBreakdown: campaignHourOfDayBreakdownSchema.optional(),
    /** Ad-schedule criterion the founder configured on Google — the
     *  windows when the campaign is allowed to serve. Empty `entries`
     *  means no schedule (24/7). Absent = legacy snapshot pre-this-field;
     *  treat the same as 24/7 for the purpose of agent reads. */
    adSchedule: campaignAdScheduleSchema.optional(),
    adGroups: z.array(adGroupSnapshotSchema).min(0).max(50),
    ads: z.array(adCreativeSnapshotSchema).min(0).max(50),
    /** [task dd14991] Assets attached to this campaign. EMPTY AND ABSENT MEAN
     *  DIFFERENT THINGS and the difference is the whole point of the field:
     *  `[]` is "we looked and google has none", which is the finding; absent is
     *  "we did not look", which every snapshot written before this is. A reader
     *  that collapses them would report a brand-new gap on every legacy row. */
    assets: z.array(campaignAssetSnapshotSchema).min(0).max(200).optional(),
    /** Anything the agent might want that doesn't fit the structured shape.
     *  Stay jsonb-flexible — provider quirks shouldn't force schema migrations. */
    providerRaw: z.record(z.string(), z.unknown()).optional(),
});
/** Stable string identity for a takeover action — used by the
 *  takeover-recommendation dismissals table. Format:
 *  `{execution.kind}:{primary-target-id}`. Two actions with the same
 *  signature describe the same operation on the same resource (e.g.
 *  pause_ad:111 is the same regardless of how mag worded the title
 *  this refresh). Caller must guarantee an execution block is
 *  present — text-only actions don't go through dismissal (they
 *  route to the followups inbox). */
export function takeoverActionSignature(execution) {
    switch (execution.kind) {
        case 'pause_campaign':
        case 'enable_campaign':
        case 'pause_ad_group':
        case 'enable_ad_group':
        case 'pause_ad':
        case 'enable_ad':
        case 'adjust_budget':
        case 'adjust_ad_group_bid':
        case 'swap_rsa':
            return `${execution.kind}:${execution.resourceId}`;
        case 'pause_keyword':
            // The criterion id is only unique WITHIN its ad group, so the
            // parent has to be part of the identity or two keywords in
            // different ad groups could collide on the dismiss log.
            return `pause_keyword:${execution.adGroupResourceId}:${execution.resourceId}`;
        case 'create_ad_group':
            // The campaign id alone is not enough — mag can propose more
            // than one new ad group on the same campaign. Mix in the draft
            // name (lowercased + slugged) so each "create ad group X" /
            // "create ad group Y" stays distinct on the dismiss log.
            return `create_ad_group:${execution.campaignResourceId}:${slugForSignature(execution.draftName)}`;
        case 'create_rsa':
            // Same logic — a single ad group can host multiple draft RSAs.
            // Mix in the first headline's text to keep separate proposals
            // separate. RSAs require >=3 headlines so [0] is always safe.
            return `create_rsa:${execution.adGroupResourceId}:${slugForSignature(execution.draftHeadlines[0]?.text ?? '')}`;
    }
}
function slugForSignature(input) {
    return input
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}
/** Compact list-row view of a campaign — used by the campaign picker on
 *  the import screen. No ad-group / creative detail, just enough to show
 *  a sortable list. */
export const campaignListItemSchema = z.object({
    providerCampaignId: z.string(),
    name: z.string(),
    type: z.string(),
    status: z.enum(['enabled', 'paused', 'removed']),
    dailyBudgetUsd: z.number(),
    /** Spend in the last 30 days. Lets the founder spot which is the
     *  biggest live campaign without opening it. */
    costUsd30d: z.number(),
});
// ─── Takeover read (Mag's analysis) ──────────────────────────────────────
/** s207 — executable spec attached to a takeover action item so the
 *  founder can one-click approve mag's recommendations from the
 *  takeover view. When the LLM identifies a concrete target in the
 *  snapshot (specific campaign / ad group / ad / budget id) it emits
 *  this `execution` block alongside the text; the dashboard then
 *  renders an "approve" button that dispatches the matching Phase
 *  B write (or opens the swap modal when the action is an RSA
 *  rewrite). Actions WITHOUT execution stay text-only — mag's
 *  general direction without a directly-runnable mutation. */
export const takeoverActionExecutionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('pause_campaign'),
        /** providerCampaignId from the snapshot. */
        resourceId: z.string().min(1).max(120),
    }),
    z.object({ kind: z.literal('enable_campaign'), resourceId: z.string().min(1).max(120) }),
    z.object({
        kind: z.literal('pause_ad_group'),
        /** providerAdGroupId from the snapshot. */
        resourceId: z.string().min(1).max(120),
    }),
    z.object({ kind: z.literal('enable_ad_group'), resourceId: z.string().min(1).max(120) }),
    z.object({
        kind: z.literal('pause_ad'),
        /** providerAdId from the snapshot. */
        resourceId: z.string().min(1).max(120),
    }),
    z.object({ kind: z.literal('enable_ad'), resourceId: z.string().min(1).max(120) }),
    z.object({
        kind: z.literal('adjust_budget'),
        /** Numeric campaign_budget resource id — usually rides on
         *  snapshot.providerRaw.budgetResourceId for the campaign. */
        resourceId: z.string().min(1).max(120),
        /** Whole-dollar new daily budget. Same cap as the write endpoint. */
        newDailyBudgetUsd: z.number().int().min(1).max(10_000),
    }),
    z.object({
        /** Spec 3c98725c — raise or lower an ad group's max CPC, the action
         *  behind an underbid finding. Ad-group level, matching where the
         *  bid actually lives (effective_cpc_bid_source = AD_GROUP). */
        kind: z.literal('adjust_ad_group_bid'),
        /** Numeric ad_group resource id. */
        resourceId: z.string().min(1).max(120),
        /** New max CPC. Same [0.05, 20] bounds as the create path, so a
         *  health recommendation can never propose a bid the create path
         *  would have rejected. */
        newCpcBidUsd: z.number().min(0.05).max(20),
    }),
    z.object({
        /** Spec 3c98725c — pause one keyword. PAUSE only; there is
         *  deliberately no remove_keyword member, because removal is
         *  irreversible on google's side and takes the keyword's history
         *  with it. Reversibility matters most for the advice we are least
         *  sure of. */
        kind: z.literal('pause_keyword'),
        /** Numeric criterion id. */
        resourceId: z.string().min(1).max(120),
        /** Parent ad group — ad-group criteria are addressed by a compound
         *  {ad_group_id}~{criterion_id} resource name. */
        adGroupResourceId: z.string().min(1).max(120),
    }),
    z.object({
        kind: z.literal('swap_rsa'),
        /** providerAdId from the snapshot — the ad to replace. */
        resourceId: z.string().min(1).max(120),
        /** Parent ad group id — needed by the swap endpoint. */
        adGroupResourceId: z.string().min(1).max(120),
        /** [12.2L-109] When mag carries a draft, swap_rsa becomes
         *  autopilot-eligible (default-on like every other execution
         *  kind) and the dashboard's swap modal pre-fills with the
         *  draft instead of the existing copy. When the drafts are
         *  absent (legacy mag_reads written before this field landed),
         *  the action stays manual and the modal falls back to the
         *  existing-copy pre-fill so the founder can hand-rewrite.
         *  All three draft fields are present together or all absent —
         *  mag emits a complete proposal or none at all. */
        draftHeadlines: z
            .array(z.object({
            text: z.string().min(1).max(30),
            pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
        }))
            .min(3)
            .max(15)
            .optional(),
        draftDescriptions: z
            .array(z.object({ text: z.string().min(1).max(90) }))
            .min(2)
            .max(4)
            .optional(),
        draftFinalUrl: z.string().url().optional(),
    }),
    /** s209 — mag drafts a brand-new ad group on the campaign. The
     *  dashboard opens the new-ad-group modal pre-filled with this
     *  draft so the founder reviews/edits before shipping (creates
     *  are net-new spend → human-in-the-loop). */
    z.object({
        kind: z.literal('create_ad_group'),
        /** providerCampaignId from the snapshot. */
        campaignResourceId: z.string().min(1).max(120),
        /** Founder-facing ad group name mag suggests. */
        draftName: z.string().min(1).max(160),
        /** CPC bid in USD — same range the write endpoint accepts. */
        draftCpcBidUsd: z.number().min(0.05).max(20),
        /** Initial keyword set. Empty array is allowed (founder can add
         *  in the modal) but mag should default to seeding at least a
         *  handful so the suggestion is actionable. */
        draftKeywords: z
            .array(z.object({
            text: googleKeywordTextSchema,
            matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
        }))
            .max(30),
        /** Optional first RSA — common pattern for search ad groups.
         *  Same shape as the create_rsa variant's draft, inlined to keep
         *  the discriminated union flat. */
        draftInitialAd: z
            .object({
            headlines: z
                .array(z.object({
                text: z.string().min(1).max(30),
                pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
            }))
                .min(3)
                .max(15),
            descriptions: z
                .array(z.object({ text: z.string().min(1).max(90) }))
                .min(2)
                .max(4),
            finalUrl: z.string().url(),
        })
            .optional(),
    }),
    /** s209 — mag drafts a brand-new RSA on an existing ad group. The
     *  dashboard opens the new-rsa modal pre-filled with the draft;
     *  founder reviews + ships. Additive (existing ads keep running). */
    z.object({
        kind: z.literal('create_rsa'),
        /** providerAdGroupId from the snapshot — the ad group the new
         *  RSA lands in. */
        adGroupResourceId: z.string().min(1).max(120),
        /** Headlines, descriptions, final URL — all founder-editable in
         *  the modal before submit. */
        draftHeadlines: z
            .array(z.object({
            text: z.string().min(1).max(30),
            pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
        }))
            .min(3)
            .max(15),
        draftDescriptions: z
            .array(z.object({ text: z.string().min(1).max(90) }))
            .min(2)
            .max(4),
        draftFinalUrl: z.string().url(),
    }),
]);
/** Optional archival context the dashboard attaches to every Google
 *  takeover write so the api can record a `takeover_action_executions`
 *  row alongside the mutation. Carries the exact title + rationale
 *  the founder saw when they clicked approve (decoupled from mag's
 *  next read, which may reword the action) plus the imported-campaign
 *  scope. Backward-compatible: writes without this field still
 *  succeed; they just don't generate an impact row, and the "how's
 *  it doing?" panel for that action will be unavailable. */
export const takeoverActionContextSchema = z.object({
    importedCampaignId: z.string().uuid(),
    title: z.string().min(1).max(120),
    rationale: z.string().min(1).max(500),
    execution: takeoverActionExecutionSchema,
});
export const takeoverActionItemSchema = z.object({
    /** Short imperative line — "pause the competitor ad group", "raise daily
     *  budget to $80". Lowercase, mag voice. */
    title: z.string().min(1).max(120),
    /** One short paragraph: why this, what mag thinks it'll do. */
    rationale: z.string().min(1).max(500),
    /** Mag's blast-radius estimate so the founder can read the asks at a
     *  glance. "low" = reversible / cheap to try; "high" = touches budget or
     *  pauses ads with traffic. */
    blastRadius: z.enum(['low', 'medium', 'high']),
    /** s207 — optional executable spec. When present, the dashboard
     *  shows an "approve" button that fires the matching Phase B
     *  write. When absent, the action stays text-only — mag's
     *  general guidance with no directly-runnable mutation. */
    execution: takeoverActionExecutionSchema.optional(),
    /** Unification roadmap Phase 5 — when true AND the campaign's
     *  autopilot is ON, the imported-autopilot scheduler can apply
     *  this action without founder approval. Agent sets this only on
     *  safe-by-construction moves: pausing an obvious-loss ad/ad-group,
     *  or a budget cut within campaigns.daily_budget_usd_cents.
     *  NEVER on creates, swaps, RSA generation, or budget RAISES.
     *  Optional + default-off so legacy reads (pre-Phase-5) stay
     *  inert. */
    autoApplyEligible: z.boolean().optional(),
});
/** [12.2L-99] Single source of truth for "would autopilot execute this
 *  action on its own?" Used by the imported-autopilot scheduler (api),
 *  the takeover view's footnote, and the home page's receipts —
 *  keeping the rule in one place so the founder can't see one badge
 *  here and another there for the same action.
 *
 *  AIaS framing: when the founder turns autopilot on, they've delegated
 *  execution to mag. The default is "mag acts." The founder reads what
 *  mag did via the audit log / receipts surface — they don't review
 *  proposals up front. So this returns true for any action mag can
 *  ship, EXCEPT in three cases:
 *
 *    - No execution block → never. The action is text-only guidance
 *      (e.g. "audit conversion tracking" — mag can't push that).
 *    - Agent set autoApplyEligible: false → never. Mag opts OUT
 *      per-action when there's genuine ambiguity worth a founder eye
 *      (e.g., pausing the only converter). The action still ships
 *      with an approve button.
 *    - kind === 'swap_rsa' → not yet. The execution block doesn't
 *      carry draft copy (the dashboard modal opens with EXISTING
 *      copy and the founder edits), so the autopilot tick has
 *      nothing to push. Stays manual until a schema extension. */
export function isAutoApplyEligible(action) {
    if (!action.execution)
        return false;
    if (action.autoApplyEligible === false)
        return false;
    // [12.2L-109] swap_rsa is autopilot-eligible only when mag carries
    // a complete draft (headlines + descriptions + url). Without the
    // draft, the autopilot tick has nothing to push — the action stays
    // manual and the dashboard modal opens with existing-copy pre-fill.
    if (action.execution.kind === 'swap_rsa') {
        const e = action.execution;
        return (e.draftHeadlines !== undefined &&
            e.draftDescriptions !== undefined &&
            e.draftFinalUrl !== undefined);
    }
    return true;
}
/** Unification roadmap Phase 4 — recommended daily budget mag
 *  suggests for an imported campaign, derived from the snapshot's
 *  30-day spend + the brand's autonomous cap. The takeover view
 *  surfaces this as a conversation card and persists the founder's
 *  pick onto campaigns.daily_budget_usd_cents. Separate agent from
 *  takeover-read so the takeover-read prompt stays focused on
 *  headline/whatsWorking/whatsBroken/actions. */
export const budgetRecommendationSchema = z.object({
    /** Mag's recommended daily budget in USD cents. Integer.
     *  Constrained 100–500000 ($1–$5000/day) so a hallucinated
     *  zero or absurd number can't slip through and reach the api. */
    recommendedDailyBudgetUsdCents: z.number().int().min(100).max(500_000),
    /** What the founder is currently spending (per the snapshot) — also
     *  in cents. Mag echoes this back so the dashboard renders a
     *  "current $X / recommended $Y" pair without re-deriving. */
    currentDailyBudgetUsdCents: z.number().int().min(0).max(500_000),
    /** One mag-voice sentence explaining the call. Lowercase, "i"
     *  not "we", cites the snapshot metric that drove the number
     *  (e.g. "$54/day with 0 conversions in 30 days — i'd cut to
     *  $20 while we fix the keyword targeting"). */
    rationale: z.string().min(1).max(280),
});
/** Deterministic red-flag findings the campaign reviewer attaches
 *  to a takeover read at import time. NOT generated by an LLM —
 *  these are clear numeric / structural triggers (disapproved ad,
 *  URL HEAD returns 4xx, ad group with <2 ads, etc.). The
 *  takeover view renders them in a "what i found" section BEFORE
 *  mag's proposed actions so the founder reads "what's broken"
 *  before "what to do about it." */
export const importFindingKindSchema = z.enum([
    /** At least one ad on this campaign is disapproved in google ads
     *  (severity LIMITED / FULLY_LIMITED / PROHIBITED). */
    'policy_disapproved',
    /** Ad finalUrl returns 4xx/5xx to the AdsBot user agent — google
     *  will disapprove it on the next review cycle. */
    'url_unreachable',
    /** Ad group has 1 or 0 ads — google's optimizer needs ≥2 to test. */
    'thin_ad_group',
    /** Ad with ≥1k impressions has CTR < 0.3% — below the search-ads
     *  floor. Surfacing as a finding, not an auto-pause, because the
     *  founder might be running awareness intentionally. */
    'low_ctr',
    /** Ad / ad group has spent ≥$50 in the last 30d with zero
     *  attributed conversions. Worth a founder eyeball. */
    'spend_no_convs',
    /** Campaign has no sitelinks / callouts / structured snippets —
     *  ad extensions lift CTR materially and most imported campaigns
     *  don't have them. */
    'missing_extensions',
]);
export const importFindingSchema = z.object({
    kind: importFindingKindSchema,
    /** 'high' for things blocking ads from serving (disapproved,
     *  url broken). 'medium' for things degrading performance
     *  (thin ad group, low CTR). 'low' for things worth noting but
     *  not urgent (missing extensions). */
    severity: z.enum(['high', 'medium', 'low']),
    /** One-line mag-voice description, ≤200 chars. Renders inline
     *  on the takeover view. */
    description: z.string().min(1).max(200),
    /** Optional google resource id this finding targets (ad,
     *  ad group, campaign). Lets the renderer deep-link to the row
     *  inside the campaign detail panel. */
    resourceId: z.string().max(120).optional(),
    /** Optional human label for the resource (ad name, ad group
     *  name) — same shape we already show in the snapshot lists so
     *  the founder reads "the 'unnamed ad' ad" not a numeric id. */
    resourceLabel: z.string().max(160).optional(),
});
/** Mag's full read on a campaign she's just imported. Renders as the
 *  takeover review screen (cf. /campaigns/imported/:id). */
export const takeoverReadSchema = z.object({
    /** 2–3 sentence opener, mag voice. "this is a brand-search campaign,
     *  $50/day, mostly working — here's the part that isn't." */
    headline: z.string().min(1).max(600),
    /** What's working — short bullets. */
    whatsWorking: z.array(z.string().min(1).max(280)).min(1).max(6),
    /** What's not working — short bullets. */
    whatsBroken: z.array(z.string().min(1).max(280)).min(0).max(6),
    /** Concrete action items mag would queue up. Ordered by recommended
     *  sequence. */
    actions: z.array(takeoverActionItemSchema).min(0).max(8),
    /** A one-line summary mag uses as the imported_campaigns row title in
     *  the dashboard list. Lowercase, no period. */
    shortTitle: z.string().min(1).max(80),
    /** Unification roadmap Phase 4 — mag's recommended daily budget +
     *  rationale. Produced by a separate budget-recommender agent and
     *  attached to the takeover-read object server-side before persist.
     *  Optional so legacy imports (pre-Phase-4) parse cleanly without
     *  a backfill. Surfaces as a conversation card on the takeover
     *  view; founder's pick persists onto campaigns.daily_budget_usd_cents. */
    budgetRecommendation: budgetRecommendationSchema.optional(),
    /** Deterministic red-flag findings produced by the import-review
     *  pass. Optional so legacy imports parse cleanly. Renders as
     *  "↘ what i found" above the actions list. Ordered by severity
     *  desc, then by kind. */
    findings: z.array(importFindingSchema).max(20).optional(),
});
/** Provider-agnostic ad-account record. Today only Google; meta/tiktok
 *  reuse this shape. */
export const adAccountProviderSchema = adPlatformSchema;
export const adAccountStatusSchema = z.enum(['connected', 'mock', 'disconnected']);
//# sourceMappingURL=ad-platform.js.map